import { useCallback, useMemo, useState } from 'react';
import { createDocumentSelectionService } from '../lib/documentSelectionService';
import type { DocumentSelectionRequest, SelectedDocument } from '../lib/documentSelectionTypes';

type PendingDocumentSelection = {
  request: DocumentSelectionRequest;
  resolve: (value: SelectedDocument | null) => void;
};

export type DocumentPickerDrawerState = {
  open: boolean;
  request: DocumentSelectionRequest | null;
  onSelect: (document: SelectedDocument) => void;
  onCancel: () => void;
};

/**
 * Hosts universal Document Picker UI state and exposes Document Selection Service to handlers.
 */
export function useDocumentSelectionHost() {
  const [pending, setPending] = useState<PendingDocumentSelection | null>(null);

  const documentSelectionService = useMemo(
    () =>
      createDocumentSelectionService({
        requestSelection: (request) =>
          new Promise<SelectedDocument | null>((resolve) => {
            setPending({ request, resolve });
          }),
      }),
    []
  );

  const completeSelection = useCallback((document: SelectedDocument | null) => {
    if (!pending) {
      return;
    }
    pending.resolve(document);
    setPending(null);
  }, [pending]);

  const pickerState: DocumentPickerDrawerState = {
    open: pending != null,
    request: pending?.request ?? null,
    onSelect: (document) => completeSelection(document),
    onCancel: () => completeSelection(null),
  };

  return {
    documentSelectionService,
    pickerState,
  };
}

export type UseDocumentSelectionHostResult = ReturnType<typeof useDocumentSelectionHost>;
